import React, { ReactNode, useCallback, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { useIntersectionObserver } from 'usehooks-ts';
import useOnClickOutside from '@hooks/browser-events/use-on-click-outside';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import styles from './info-popover.module.scss';

export interface InfoPopoverProps {
  title: string;
  subtitle?: string;
  cta?: {
    text: string;
    onClick?: () => void;
  };
  showCloseButton?: boolean;
  children: ReactNode;
  className?: string;
  classNameModifier?: string;
  classNameWrapper?: string;
  dataCy?: string;
  onClose?: () => void;
  isVisible?: boolean;
  isRightAligned?: boolean;
  closeOnScreenClick?: boolean;
  showOnIntersection?: boolean;
  position?: 'top' | 'bottom';
  arrowPosition?: 'left' | 'center' | 'right';
}

const InfoPopover: React.FC<InfoPopoverProps> = ({
  title,
  subtitle,
  cta,
  showCloseButton = true,
  className,
  classNameModifier,
  classNameWrapper,
  dataCy,
  children,
  onClose = () => {},
  isVisible = false,
  isRightAligned = false,
  closeOnScreenClick = false,
  showOnIntersection = false,
  position = 'bottom',
  arrowPosition = 'center',
}) => {
  const { t } = useTranslation();
  const [internalIsVisible, setInternalIsVisible] = useState(false);
  const [hasBeenShown, setHasBeenShown] = useState(false);
  const intersectionRef = useRef<HTMLDivElement>(null);
  const clickOutsideRef = useRef<HTMLDivElement>(null);

  const entry = useIntersectionObserver(intersectionRef, { threshold: 0.5 });

  useEffect(() => {
    if (showOnIntersection && entry?.isIntersecting) {
      if (!hasBeenShown) {
        setInternalIsVisible(true);
        setHasBeenShown(true);
      }
    } else if (showOnIntersection && !entry?.isIntersecting && hasBeenShown && internalIsVisible) {
      setInternalIsVisible(false);
      onClose();
    }
  }, [entry?.isIntersecting, showOnIntersection, hasBeenShown, internalIsVisible, onClose]);

  const shouldShowTooltip = showOnIntersection ? internalIsVisible : isVisible;

  const handleClose = useCallback(() => {
    if (showOnIntersection) {
      setInternalIsVisible(false);
    }
    onClose();
  }, [onClose, showOnIntersection]);

  useOnClickOutside(clickOutsideRef, () => {
    if (closeOnScreenClick) {
      handleClose();
    }
  });

  const handleCtaClick = useCallback(() => {
    cta?.onClick?.();
    handleClose();
  }, [cta, handleClose]);

  const content = (
    <>
      <div>{children}</div>
      {shouldShowTooltip && (
        <div
          className={clsx(
            styles.tooltip,
            className,
            classNameModifier && styles[`tooltip--${classNameModifier}`],
            isRightAligned && styles['tooltip--right'],
            styles[`tooltip--${position}`],
          )}
          data-cy={dataCy}
          role="tooltip"
          aria-label={dataCy}
          ref={closeOnScreenClick ? clickOutsideRef : undefined}
        >
          {showCloseButton && (
            <button
              className={styles.tooltip__closeBtn}
              data-cy={`${dataCy}_closeBtn`}
              type="button"
              onClick={handleClose}
              aria-label={t('MODAL.BUTTON.CLOSE')}
            >
              <Icon
                className={styles.tooltip__closeBtn__icon}
                name="close-default"
                ariaHidden
              />
              <span className="visuallyhidden">{t('MODAL.BUTTON.CLOSE')}</span>
            </button>
          )}
          <div className={styles.tooltip__contentWrapper}>
            <div className={styles.tooltip__content}>
              <p
                className={styles.tooltip__content__title}
                data-cy={`${dataCy}_title`}
              >
                {title}
              </p>
              {subtitle && (
                <p
                  className={styles.tooltip__content__subtitle}
                  data-cy={`${dataCy}_subtitle`}
                >
                  {subtitle}
                </p>
              )}
            </div>
            {cta && (
              <button
                className={clsx(styles.tooltip__content__cta, 'vc-btn', 'vc-btn--secondary', 'vc-btn--small')}
                data-cy={`${dataCy}_cta`}
                type="button"
                onClick={handleCtaClick}
              >
                {cta.text}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div
      className={clsx(
        styles.tooltipWrapper,
        shouldShowTooltip && styles['tooltipWrapper--visible'],
        styles[`tooltipWrapper--${position}`],
        styles[`tooltipWrapper--arrow-${arrowPosition}`],
        classNameWrapper,
      )}
      ref={showOnIntersection ? intersectionRef : undefined}
      role="tooltip"
    >
      {content}
    </div>
  );
};

export default InfoPopover;
